import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

/**
 * TileGrid — ряд плиток-метрик (Погода, Рельеф и т.п.), Earth Tech DS v1.
 *
 * Спека layout-слоя: плитки в ряду ОДИНАКОВОЙ высоты, `gap-3` (12px), сетка —
 * не `flex-wrap`. `auto-rows-fr` + дефолтный `items-stretch` дают равную высоту
 * даже когда подписи разной длины (иначе ряд «плывёт» и границы плиток не
 * совпадают — исходный баг карточки поля).
 *
 * ```tsx
 * <TileGrid cols={3}>
 *   <Tile …/>  // плитке НЕ нужен h-full — грид растянет сам
 * </TileGrid>
 * ```
 */
const tileGridVariants = cva('grid auto-rows-fr gap-3', {
  variants: {
    cols: {
      2: 'grid-cols-2',
      3: 'grid-cols-3',
      4: 'grid-cols-2 sm:grid-cols-4',
    },
  },
  defaultVariants: { cols: 2 },
});

export interface TileGridProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof tileGridVariants> {}

export const TileGrid = React.forwardRef<HTMLDivElement, TileGridProps>(
  ({ className, cols, ...props }, ref) => (
    <div ref={ref} className={cn(tileGridVariants({ cols }), className)} {...props} />
  ),
);
TileGrid.displayName = 'TileGrid';

/**
 * Tile — плитка-метрика «подпись + значение» внутри `TileGrid`.
 *
 * Живёт здесь, а не в экране, потому что до AK-773 её переписывали в каждом
 * экране заново: к восьмой копии разъехались и внутренний отступ, и размер
 * значения, и наличие подписи снизу — то есть ряды плиток на соседних экранах
 * перестали выглядеть одним контролом (ревью head-web по #966/#967).
 *
 * `size="lg"` — плитка-итог (₽/га карты, число обработок), `default` — обычная
 * характеристика. Высоту не задаёт: её растягивает `auto-rows-fr` грида.
 *
 * Исторические локальные копии (spp, tech-cards/CostSummary, plan-variant,
 * sowing-plan, structure/farm-card, analytics/history) СОЗНАТЕЛЬНО не мигрированы
 * этим PR — миграция шести экранов чужой вертикали в PR про модули техкарты
 * означала бы правку без ревью их владельцев.
 */
const tileValueVariants = cva('text-foreground', {
  variants: {
    size: {
      default: 'text-base font-medium',
      lg: 'text-2xl font-semibold',
    },
  },
  defaultVariants: { size: 'default' },
});

export interface TileProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof tileValueVariants> {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Пояснение под значением — «в этой карте», «без обработки №4». */
  hint?: React.ReactNode;
}

export const Tile = React.forwardRef<HTMLDivElement, TileProps>(
  ({ className, size, label, value, hint, ...props }, ref) => (
    <Card ref={ref} className={className} {...props}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={cn('mt-1', tileValueVariants({ size }))}>{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  ),
);
Tile.displayName = 'Tile';

export { tileGridVariants, tileValueVariants };
